import React, { useEffect, FC } from 'react';

import { Box, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { BorderedBox, getIcon, StackedHeader } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { DataSecurityFilters } from 'containers/Visibility/Data/Components/DataTable/DataSecurityFilters';
import { useDataDashboardContext } from 'containers/Visibility/Data/Components/hooks/context';
import { useDataTableColumns } from 'containers/Visibility/Data/Components/hooks/useDataTableColumns';
import { selectDataDashboard } from 'containers/Visibility/Data/selectors';
import { actions } from 'containers/Visibility/Data/slice';

interface Props {
  isOpen: boolean;
  onClose(): void;
  currentRecord: Record<string, any>;
}

export const SourcesByDataType: FC<Props> = props => {
  const { currentRecord, isOpen, onClose } = props;
  const { sensitiveSourcesByDataType } = useSelector(selectDataDashboard);
  const {
    dataTypeSourcesColumns,
    tableActionModals,
    handleRowClick,
  } = useDataTableColumns(MetadataTabs.Data);
  const dispatch = useDispatch();

  const { getDataTypeSources } = useDataDashboardContext();

  useEffect(() => {
    isOpen && getDataTypeSources(currentRecord?.name, true);
  }, [getDataTypeSources, currentRecord, isOpen]);

  return (
    <Drawer
      styles={{
        drawer: {
          blockScrollOnMount: false,
          trapFocus: false,
        },
        content: { px: 0, maxW: '1600px', h: 'full' },
      }}
      isOpen={isOpen}
      onClose={onClose}
      closeButton
      header={
        <StackedHeader
          upper="Sensitive Sources"
          lower={currentRecord?.sensitive_data_name}
          type={currentRecord?.name}
        />
      }
      body={
        <BorderedBox flex={1} w="full" h="full" p={4}>
          <Stack w="full" h="full" spacing={5}>
            <DataSecurityFilters
              isExpanded
              filterTab="DataSources"
              filterScope="drawer"
              showActions={false}
              showDataTypeFilter={false}
            />
            <Box flex={1} maxW="100%">
              <Table
                data={sensitiveSourcesByDataType.data}
                columns={dataTypeSourcesColumns}
                isLoading={sensitiveSourcesByDataType.isLoading}
                // Make each source row open its resource metadata on the Data tab.
                cursor="pointer"
                styles={{ header: { position: 'relative', zIndex: 800 } }}
                onRowClick={row => handleRowClick(row)}
                pagination={{
                  totalCount: sensitiveSourcesByDataType?.page.totalCount,
                  pageInfo: sensitiveSourcesByDataType.page.info,
                  onChange: pageInfo => {
                    dispatch(
                      actions.updateSensitiveSourcesByDataTypePageInfo(
                        pageInfo,
                      ),
                    );
                  },
                }}
              />
            </Box>
            {tableActionModals}
          </Stack>
        </BorderedBox>
      }
    />
  );
};
